import React, { useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { DraggableProvided, DraggableStateSnapshot, DroppableProvided, DropResult } from '@hello-pangea/dnd'
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd'

import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import Container from '@mui/material/Container'
import Button from '@mui/material/Button'
import MuiLink from '@mui/material/Link'
import Backdrop from '@mui/material/Backdrop'
import CircularProgress from '@mui/material/CircularProgress'
import TableContainer from '@mui/material/TableContainer'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Stack from '@mui/material/Stack'

import Alert from '../components/alert/alert'
import { useContextHook } from '../store/context'
import { Profile } from '../types/profile'
import { get, isOsxSafari, post, remove, setPageTitle } from '../utils/service'
import { PagedResponse } from '../types/pagedResponse'
import { MachineSlot } from '../types/machineslot'
import ListHeader from '../components/listheader/listheader'
import { getNextFreeSlotIndex, isValidProfileTypeForMachine } from '../utils/profileEditorUtils'
import ProfileRow from '../components/profiles/profileRow'
import { Machine } from '../types/machine'
import { setCurrentMachine } from '../store/actions'
import Pagination from '../components/fields/pagination'
import SortableTableHead from '../components/fields/sortableTableHead'
import { SortType } from '../types/enums/sortType'
import { Typography } from '@mui/material'
import ProfileBulkOp from '../components/profiles/profileBulkOp'
import AssignDialog from '../components/profiles/assignDialog'
import ProfileBulkOpFeedback from '../components/profiles/profileBulkOpFeedback'
import DeleteDialog from '../components/profiles/deleteDialog'

const getLoadProfilesUrl = (customerId: number, searchString: string): string => {
  const searchFragment = searchString ? `&search=${searchString}` : ''
  return `profiles/?customer=${customerId}${searchFragment}`
}
const ProfilesView = (): JSX.Element => {
  const { t } = useTranslation()
  const { state, dispatch } = useContextHook()
  const theme = useTheme()
  const pageScrollTopRef = React.useRef<HTMLTableRowElement>(null)

  const [profiles, setProfiles] = React.useState<Array<Profile>>([])
  const [machineSlots, setMachineSlots] = React.useState<Array<MachineSlot>>([])
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [isFirstLoadDone, setIsFirstLoadDone] = React.useState<boolean>(false)
  const [hasLoadError, setHasLoadError] = React.useState<boolean>(false)
  const [isSaveMaskVisible, setIsSaveMaskVisible] = React.useState<boolean>(false)
  const [loadProfilesUrl, setLoadProfilesUrl] = React.useState<string>('')
  const [paginationSettings, setPaginationSettings] = React.useState<PagedResponse<Profile> | null>(null)
  const [showAssignDialog, setShowAssignDialog] = React.useState<boolean>(false)
  const [showAssignBbpDialog, setShowAssignBbpDialog] = React.useState<boolean>(false)
  const [showDeleteDialog, setShowDeleteDialog] = React.useState<boolean>(false)
  const [sortFragment, setSortFragment] = React.useState<string>('')
  const [searchString, setSearchString] = React.useState<string>('')
  const [selectedProfiles, setSelectedProfiles] = React.useState<Profile[]>([])
  const [showFeedbackAlert, setShowFeedbackAlert] = React.useState<boolean>(false)
  const [assignedProfilesAlert, setAssignedProfilesAlert] = React.useState<Profile[]>([])
  const [unassignedProfilesAlert, setUnassignedProfilesAlert] = React.useState<Profile[]>([])
  const [assignedBbpAlert, setAssignedBbpAlert] = React.useState<Profile | undefined>(undefined)
  const [unassignedBbpAlert, setUnassignedBbpAlert] = React.useState<Profile | undefined>(undefined)
  const [error, setError] = React.useState<string | undefined>(undefined)

  const isScreenBelowMdBreakpoint = useMediaQuery(theme.breakpoints.down('md'))

  const pageLoaded = React.useCallback((profilesResponse: PagedResponse<Profile>): void => {
    setIsLoading(false)
    setPaginationSettings(profilesResponse)
    setProfiles(profilesResponse.results)
  }, [])

  const loadProfiles = useCallback(
    async (searchString: string): Promise<void> => {
      try {
        const results = await Promise.all([
          get<PagedResponse<Profile>>(getLoadProfilesUrl(state.currentCustomer?.id || 0, searchString)),
          get<PagedResponse<MachineSlot>>(`machineslots/?machine=${state.currentMachine?.id || 0}`),
        ])

        const [profilesResponse, machineSlotResponse] = results
        pageLoaded(profilesResponse.data)

        // machineslots should only contain items which are assigned, not previously assigned->unassigned items from the database
        setMachineSlots(machineSlotResponse.data.results.sort((a, b) => a.slot_index - b.slot_index))
      } catch (error) {
        setHasLoadError(true)
        setIsLoading(false)
        setIsFirstLoadDone(true)
      }
    },
    [pageLoaded, state.currentCustomer?.id, state.currentMachine?.id]
  )

  React.useEffect(() => {
    if (state.currentMachine?.id && state.currentCustomer?.id) {
      setIsLoading(true)
      setHasLoadError(false)
      setIsFirstLoadDone(false)
      setSelectedProfiles([])
      // load all profiles, to determine which are assigned to machine
      loadProfiles('').then(() => {
        setIsLoading(false)
        setIsFirstLoadDone(true)
      })
    }
  }, [state.currentMachine?.id, state.currentCustomer?.id, loadProfiles])

  React.useEffect(() => {
    if (loadProfilesUrl) {
      setIsLoading(true)
      setProfiles([])
      if (pageScrollTopRef.current) {
        pageScrollTopRef.current.focus()
      }
      get<PagedResponse<Profile>>(loadProfilesUrl)
        .then((profilesResponse) => {
          pageLoaded(profilesResponse.data)
        })
        .catch(() => {
          setHasLoadError(true)
          setIsLoading(false)
        })
    }
  }, [loadProfilesUrl, pageLoaded])

  // update page title
  React.useEffect(() => {
    if (state.currentMachine?.name) {
      setPageTitle(`${state.currentMachine.name} ${t('profilesview.profiles')}`)
    }
  }, [state.currentMachine?.name, t])

  React.useEffect(() => {
    if (state.currentCustomer?.id) {
      setPaginationSettings(null)
      const sortFragmentString = sortFragment ? `&ordering=${sortFragment}` : ''
      setLoadProfilesUrl(`${getLoadProfilesUrl(state.currentCustomer.id, searchString)}${sortFragmentString}`)
    }
  }, [state.currentCustomer?.id, searchString, sortFragment])

  const onSort = (newSortFragment: string): void => {
    setSortFragment(newSortFragment)
  }
  const getAssignedSlots = (): Array<MachineSlot> => {
    return machineSlots.filter((x) => !!x.requested_profile_data)
  }

  const handleUnassign = (selected: Profile[], dontRenderAlert?: boolean) => {
    clearFeedback()
    const bbp = selected.find((x) => x.is_bbp_profile)
    if (bbp) {
      sendUnassignBbpProfileRequest()
    }
    const slotsToUnassign = machineSlots.filter((x) => selected.find((p) => x.requested_profile === p.url))
    const slotIndexes = slotsToUnassign.map((slot) => slot.slot_index)
    if (slotIndexes.length > 0) {
      const url = `/machineslots/unassign/`
      post<any, MachineSlot[]>(url, { slots: slotIndexes, machine_id: state.currentMachine?.id })
        .then((machineSlotResponse) => {
          setMachineSlots(
            machineSlotResponse.data.sort(
              (a: { slot_index: number }, b: { slot_index: number }) => a.slot_index - b.slot_index
            )
          )
          setUnassignedProfilesAlert(selected.filter((p) => !p.is_bbp_profile))
          setSelectedProfiles([])
        })
        .catch(() => setError(t('profilesview.unassignerror')))
    }
    if (dontRenderAlert) {
      return
    } else {
      setShowFeedbackAlert(true)
    }
  }
  const handleAssign = (profiles: Profile[]) => {
    clearFeedback()
    const bbps = profiles.filter((p) => p.machine_slot_index === -1)
    if (bbps.length === 1) {
      sendAssignBbpRequest(bbps[0])
      const newList = selectedProfiles.filter((p) => p.machine_slot_index !== -1)
      setSelectedProfiles(newList)
    }
    if (bbps.length > 1) {
      setShowAssignBbpDialog(true)
    }
    profiles = profiles.filter((p) => p.machine_slot_index !== -1)
    if (
      profiles.some((p) => p.machine_slot_index === undefined) ||
      profiles.length > (state.currentMachine?.profile_slot_count || 0)
    ) {
      setShowAssignDialog(true)
    } else if (profiles.length > 0) {
      sendAssignProfilesRequest(profiles)
    }
    setShowFeedbackAlert(true)
  }

  const sendAssignProfilesRequest = (profiles: Profile[]) => {
    const url = `/machineslots/assign/`
    post<any, MachineSlot[]>(url, { profiles: profiles, machine_id: state.currentMachine?.id })
      .then((machineSlotResponse) => {
        setMachineSlots(
          machineSlotResponse.data.sort(
            (a: { slot_index: number }, b: { slot_index: number }) => a.slot_index - b.slot_index
          )
        )
        const newList = selectedProfiles.filter((p) => p.is_bbp_profile)
        setSelectedProfiles(newList)
        setShowAssignDialog(false)
        setAssignedProfilesAlert(profiles)
      })
      .catch(() => setError(t('profilesview.assignerror')))
  }
  const deleteProfiles = () => {
    const url = `/profiles/delete_multiple/`
    const profiles = selectedProfiles
    const profile_ids = profiles.map((p) => p.id)
    const slots = profiles.filter((p) => machineSlots.find((m) => m.requested_profile_data === p))
    const bbp = profiles.find((p) => p.is_bbp_profile)
    if (bbp) {
      sendUnassignBbpProfileRequest()
    }
    post(url, { profile_ids: profile_ids, customer_id: state.currentCustomer?.id })
      .then(() => {
        setShowDeleteDialog(false)
        loadProfiles(searchString)
        if (slots.length > 0) {
          handleUnassign(slots, true)
        }
      })
      .catch(() => {
        clearFeedback()
        setShowDeleteDialog(false)
        setError(t('profilesview.deleteerror'))
        setShowFeedbackAlert(true)
      })
  }

  const handleProfileSelection = (profile: Profile) => {
    if (selectedProfiles.includes(profile)) {
      setSelectedProfiles(selectedProfiles.filter((p) => p.url !== profile.url))
    } else {
      let slot_index: number | undefined = undefined
      if (profile.is_bbp_profile) slot_index = -1
      else slot_index = getNextFreeSlotIndex(state.currentMachine, machineSlots, selectedProfiles)
      profile.machine_slot_index = slot_index
      setSelectedProfiles([...selectedProfiles, profile])
    }
  }

  const sendUnassignBbpProfileRequest = (): void => {
    const bbp = state.currentMachine?.bbp_profile
    setIsSaveMaskVisible(true)
    remove<Machine>(`${state.currentMachine?.url}bbp_profile/`)
      .then((response) => {
        setUnassignedBbpAlert(bbp || undefined)
        setSelectedProfiles([])
        setIsSaveMaskVisible(false)
        dispatch(setCurrentMachine(response.data))
      })
      .catch(() => setError(t('profilesview.bbpunassignerror')))
  }

  const clearFeedback = () => {
    setShowFeedbackAlert(false)
    setAssignedBbpAlert(undefined)
    setUnassignedBbpAlert(undefined)
    setError(undefined)
    setAssignedProfilesAlert([])
    setUnassignedProfilesAlert([])
  }

  const updateUnassignedProfilesAlert = (profiles: Profile[]) => {
    setUnassignedProfilesAlert(profiles)
  }

  const sendAssignBbpRequest = (profile: Profile): void => {
    const old_bbp = state.currentMachine?.bbp_profile
    const newList = selectedProfiles.filter((p) => !p.is_bbp_profile)
    setSelectedProfiles(newList)
    post<{ bbp_profile: string }, Machine>(`${state.currentMachine?.url}bbp_profile/`, { bbp_profile: profile.url })
      .then((response) => {
        setShowAssignBbpDialog(false)
        dispatch(setCurrentMachine(response.data))
        setUnassignedBbpAlert(old_bbp || undefined)
        setAssignedBbpAlert(profile)
      })
      .catch(() => setError(t('profilesview.bbpassignerror')))
  }
  const renderTableRow = (profile: Profile): JSX.Element => {
    // check if this profile is assigned:
    const assignedMachineSlot = machineSlots.find((x) => x.requested_profile === profile.url)
    // check if this profile is downloaded to machine (we do not care which slot it is in)
    const loadedSlot = machineSlots.find((x) => x.loaded_profile && x.loaded_profile === profile.url)

    return (
      <ProfileRow
        profile={profile}
        selectedProfiles={selectedProfiles}
        currentCustomer={state.currentCustomer}
        isAssigned={!!assignedMachineSlot || profile.url === state.currentMachine?.bbp_profile?.url}
        isBBP={profile.is_bbp_profile}
        assignedMachineSlotNumber={assignedMachineSlot ? assignedMachineSlot.slot_index + 1 : 0}
        downloadedTimestamp={loadedSlot?.loaded_timestamp}
        canBeAssignedToMachine={isValidProfileTypeForMachine(profile, state.currentMachine)}
        handleProfileSelection={() => handleProfileSelection(profile)}
        createdBy={profile.author_name}
        profileLink={
          isOsxSafari() ? (
            <MuiLink component={Link} to={`/machine/${state.currentMachine?.slug}/profiles/${profile.id}`}>
              {profile.name}
            </MuiLink>
          ) : (
            <>
              <Button
                className="link-cell"
                sx={{
                  right: isScreenBelowMdBreakpoint ? 0 : '88px !important',
                  bottom: isScreenBelowMdBreakpoint ? '52px !important' : 0,
                }}
                component={Link}
                to={`/machine/${state.currentMachine?.slug}/profiles/${profile.id}`}
              />
              {profile.name}
            </>
          )
        }
      />
    )
  }
  const onDragEnd = (result: DropResult): void => {
    // dropped outside the list
    if (!result.destination || result.destination.index === result.source.index) {
      return
    }
    // no movement
    if (result.destination.index === result.source.index) {
      return
    }

    const swappedArray = getAssignedSlots()
    // move the dragged item to new position
    swappedArray.splice(result.destination.index, 0, swappedArray.splice(result.source.index, 1)[0])

    // re-assign slot_index for items. Add items with changed slot_index to save payload array
    const slotsToPost: Array<MachineSlot> = []

    for (let i = 0; i < swappedArray.length; i++) {
      const indexToUse = getAssignedSlots()[i].slot_index
      const slot = {
        ...swappedArray[i],
        slot_index: indexToUse,
      }
      if (swappedArray[i].slot_index !== indexToUse) {
        slotsToPost.push(slot)
      }
    }

    Promise.all([...slotsToPost.map((slot: MachineSlot) => post<MachineSlot, MachineSlot>('machineslots/', slot))])
      .then(() => {
        // reload all machine slots instead of applying all changes manually
        get<PagedResponse<MachineSlot>>(`machineslots/?machine=${state.currentMachine?.id}`)
          .then((response) => {
            setIsSaveMaskVisible(false)
            setMachineSlots(response.data.results.sort((a, b) => a.slot_index - b.slot_index))
          })
          .catch()
      })
      .catch()

    // temp update while waiting for server response
    setMachineSlots((prevMachineSlots) => {
      return prevMachineSlots.map((x) => slotsToPost.find((y) => y.slot_index === x.slot_index) || x)
    })
    setIsSaveMaskVisible(true)
  }

  const assignedProfiles = getAssignedSlots().map((x) => x.requested_profile_data) as Array<Profile>
  const assignedProfileUrls = assignedProfiles.map((x) => x.url)
  const unassignedProfiles = profiles.filter(
    (x) => assignedProfileUrls.indexOf(x.url) === -1 && x.url !== state.currentMachine?.bbp_profile?.url
  )

  return (
    <Container component="main" maxWidth={false}>
      <Backdrop open={isSaveMaskVisible} sx={{ zIndex: 10 }}>
        <CircularProgress />
      </Backdrop>
      {hasLoadError && <Alert severity="error">{t('profilesview.loaderror')}</Alert>}
      {(showAssignDialog || showAssignBbpDialog) && (
        <AssignDialog
          onClose={() => {
            setShowAssignDialog(false)
            setShowAssignBbpDialog(false)
          }}
          selectedProfiles={selectedProfiles}
          machineSlots={machineSlots}
          assignBBP={sendAssignBbpRequest}
          handleAssign={sendAssignProfilesRequest}
          updateUnassignedProfilesAlert={updateUnassignedProfilesAlert}
        />
      )}
      {showDeleteDialog && (
        <DeleteDialog
          onClose={() => setShowDeleteDialog(false)}
          onConfirm={deleteProfiles}
          profiles={selectedProfiles}
        />
      )}
      <DragDropContext onDragEnd={onDragEnd}>
        {state.currentMachine && state.currentCustomer && (
          <Stack
            direction={'column'}
            alignItems={{ xs: 'stretch', md: 'center' }}
            width="100%"
            spacing={2}
            sx={{ position: 'sticky', top: 0, backgroundColor: 'background.default', zIndex: 1 }}
          >
            <ListHeader
              headerText={t('profilesview.profiles')}
              searchPlaceholder={t('profilesview.searchprofiles')}
              typeString={`${state.currentMachine.name} - ${paginationSettings?.count || 0} profiles`}
              totalResults={''}
              onSearch={(newSearchString: string): void => {
                if (searchString !== newSearchString) {
                  setSearchString(newSearchString)
                }
              }}
            >
              {!isScreenBelowMdBreakpoint && (
                <>
                  {selectedProfiles.length > 0 && (
                    <ProfileBulkOp
                      selectedProfiles={selectedProfiles}
                      machineSlots={machineSlots}
                      handleDelete={() => setShowDeleteDialog(true)}
                      handleUncheck={() => {
                        setSelectedProfiles([])
                      }}
                      handleAssign={() => handleAssign(selectedProfiles)}
                      handleUnassign={() => handleUnassign(selectedProfiles)}
                    />
                  )}
                  <Button
                    variant="contained"
                    component={Link}
                    to={`/machine/${state.currentMachine?.slug}/profiles/create`}
                  >
                    {t('profilesview.createnewprofile')}
                  </Button>
                </>
              )}
            </ListHeader>
            {showFeedbackAlert && (
              <ProfileBulkOpFeedback
                onClose={clearFeedback}
                assignedBbp={assignedBbpAlert}
                unassignedBbp={unassignedBbpAlert}
                assignedProfiles={assignedProfilesAlert}
                unassignedProfiles={unassignedProfilesAlert}
                error={error}
              />
            )}
            {isScreenBelowMdBreakpoint && (
              <Stack direction={'row'} sx={{ pb: 1 }} spacing={1}>
                <Button
                  variant="contained"
                  component={Link}
                  to={`/machine/${state.currentMachine?.slug}/profiles/create`}
                >
                  {t('profilesview.createnewprofile')}
                </Button>
                {selectedProfiles && selectedProfiles.length > 0 && (
                  <ProfileBulkOp
                    selectedProfiles={selectedProfiles}
                    machineSlots={machineSlots}
                    handleDelete={() => setShowDeleteDialog(true)}
                    handleUncheck={() => {
                      setSelectedProfiles([])
                    }}
                    handleAssign={() => handleAssign(selectedProfiles)}
                    handleUnassign={() => handleUnassign(selectedProfiles)}
                  />
                )}
              </Stack>
            )}
          </Stack>
        )}
        {isFirstLoadDone && !hasLoadError && (
          <>
            <TableContainer sx={{ mt: 2 }}>
              {/** overflow hidden in element above to remove warning from dnd framework */}
              <Table className="colored-table collapse-table">
                <TableHead>
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant="h6">
                        {t('profilesview.profilesassignedto').replace('{0}', `${state.currentMachine?.name}`)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ height: 74 }}>
                    <TableCell>{t('profilesview.machineslot')}</TableCell>
                    <SortableTableHead
                      sortFragment={sortFragment}
                      sortProp="name"
                      sortType={SortType.String}
                      onSort={onSort}
                    >
                      {t('profilesview.name')}
                    </SortableTableHead>
                    <SortableTableHead
                      sortFragment={sortFragment}
                      sortProp="created_by"
                      sortType={SortType.String}
                      onSort={onSort}
                    >
                      {t('profilesview.createdby')}
                    </SortableTableHead>
                    <SortableTableHead
                      sortFragment={sortFragment}
                      sortProp="batch_weight"
                      sortType={SortType.Number}
                      onSort={onSort}
                    >
                      {t('profilesview.batchweight')}
                    </SortableTableHead>
                    <TableCell>{t('profilesview.profiletype')}</TableCell>
                    <SortableTableHead
                      sortFragment={sortFragment}
                      sortProp="modified"
                      sortType={SortType.Date}
                      onSort={onSort}
                    >
                      {t('profilesview.lastmodified')}
                    </SortableTableHead>
                    <TableCell className="splitter-cell" />
                    <TableCell className="action-cell-header"></TableCell>
                  </TableRow>
                </TableHead>
                <Droppable droppableId="table">
                  {(droppableProvided: DroppableProvided) => (
                    <TableBody
                      ref={(ref: HTMLElement | null) => {
                        droppableProvided.innerRef(ref)
                      }}
                      {...droppableProvided.droppableProps}
                    >
                      {assignedProfiles.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={100}>
                            <Alert severity="info">{t('profilesview.noassignedprofiles')}</Alert>
                          </TableCell>
                        </TableRow>
                      )}
                      {assignedProfiles.map((x, index) => {
                        return (
                          <Draggable draggableId={x.url} index={index} key={x.url}>
                            {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => {
                              return (
                                <TableRow
                                  key={`${x.url}-0`}
                                  className={
                                    selectedProfiles && selectedProfiles?.includes(x) ? 'selected-row' : 'colored-row'
                                  }
                                  ref={provided?.innerRef}
                                  sx={snapshot.isDragging ? { display: 'table', cursor: 'grab' } : { cursor: 'grab' }}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  tabIndex={-1}
                                >
                                  {renderTableRow(x)}
                                </TableRow>
                              )
                            }}
                          </Draggable>
                        )
                      })}
                      {droppableProvided.placeholder}
                    </TableBody>
                  )}
                </Droppable>
                <TableBody>
                  {state.currentMachine?.bbp_profile && (
                    <TableRow
                      className={
                        selectedProfiles && selectedProfiles?.includes(state.currentMachine.bbp_profile)
                          ? 'selected-row'
                          : 'colored-row'
                      }
                    >
                      {renderTableRow(state.currentMachine?.bbp_profile)}
                    </TableRow>
                  )}
                  {!state.currentMachine?.bbp_profile && (
                    <TableRow>
                      <TableCell colSpan={100}>
                        <Alert severity="info">{t('profilesview.nobbpassigned')}</Alert>
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow tabIndex={-1} ref={pageScrollTopRef}>
                    <TableCell colSpan={7}>
                      <Typography variant="h6">{t('profilesview.assigntoslot')}</Typography>
                    </TableCell>
                  </TableRow>
                  {unassignedProfiles.map((x) => {
                    return (
                      <TableRow
                        key={`${x.url}-0`}
                        className={selectedProfiles && selectedProfiles?.includes(x) ? 'selected-row' : 'colored-row'}
                      >
                        {renderTableRow(x)}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
        {isLoading && <CircularProgress />}
        <Stack sx={{ mt: 1, mb: 4 }}>
          {paginationSettings && (
            <Pagination
              isLoading={isLoading}
              paginationSettings={paginationSettings}
              onChangePage={(newPageUrl: string) => setLoadProfilesUrl(newPageUrl)}
            />
          )}
        </Stack>
        {isFirstLoadDone && !isLoading && !hasLoadError && profiles.length === 0 && (
          <Alert severity="info">{t('profilesview.noprofiles')}</Alert>
        )}
      </DragDropContext>
    </Container>
  )
}

export default ProfilesView
